import { useCallback, useEffect, useState } from 'react';
import { setupApi } from '../services/api';

/**
 * Статус мастера первого запуска (облако) — один на всё приложение.
 *
 * Спрашивают его главная («/»), Layout (режим фокуса), мастер и «С чего начать»:
 * чтобы не дёргать сервер на каждом переходе, ответ держим в модуле и раздаём
 * подписчикам. Мастер после изменений зовёт publish(), остальные видят новое сразу.
 *
 * Старый сервер (без полей focus/fresh) — читаем как раньше: фокус = «не пройден
 * и не пропущен», «свежий склад» — неизвестно (false).
 */
let cache = null;          // последний ответ сервера
let inflight = null;       // идущий запрос — второй не шлём
const subs = new Set();

export function normalizeSetup(st) {
  if (!st) return st;
  return {
    ...st,
    focus: st.focus !== undefined ? !!st.focus : (!!st.enabled && !st.done && !st.dismissed),
    fresh: !!st.fresh,
  };
}

export function publishSetup(st) {
  cache = normalizeSetup(st);
  subs.forEach((fn) => fn(cache));
}

export function fetchSetup(force = false) {
  if (cache && !force) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = setupApi.status()
    .then((r) => { publishSetup(r.data); return cache; })
    .catch(() => null)
    .finally(() => { inflight = null; });
  return inflight;
}

/** Выход из учётки — забыть статус прошлого пользователя */
export function resetSetupCache() { cache = null; }

/**
 * active=false — не спрашивать (не облако, не администратор склада).
 * Возвращает { st, loading, reload }. st=null — ещё нет ответа или не спрашивали.
 */
export function useSetupStatus(active) {
  const [st, setSt] = useState(() => (active ? cache : null));
  const [loading, setLoading] = useState(active && !cache);
  useEffect(() => {
    if (!active) { setSt(null); setLoading(false); return undefined; }
    const fn = (v) => setSt(v);
    subs.add(fn);
    if (cache) { setSt(cache); setLoading(false); }
    fetchSetup(!!cache).then((v) => { setSt(v); setLoading(false); });
    return () => { subs.delete(fn); };
  }, [active]);
  const reload = useCallback(() => fetchSetup(true), []);
  return { st, loading, reload };
}
